import { useDroppable } from '@dnd-kit/core';
import { Users, FolderOpen, ChevronDown, ChevronRight, MoreVertical, FolderPlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface DroppableTeamRootFolderProps {
  teamId: string;
  teamName: string;
  teamFolderId: string;
  workflowCount?: number;
  onClick: () => void;
  expanded?: boolean;
  onToggleExpand?: () => void;
  onAddFolder?: () => void;
  hasSubfolders?: boolean;
}

export function DroppableTeamRootFolder({
  teamId,
  teamName,
  teamFolderId,
  workflowCount,
  onClick,
  expanded = true,
  onToggleExpand,
  onAddFolder,
  hasSubfolders = false,
}: DroppableTeamRootFolderProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: `team-root-${teamId}`,
    data: {
      type: 'team-folder',
      teamFolderId: teamFolderId,
      teamId: teamId,
    },
  });

  return (
    <div className="relative">
      <div
        ref={setNodeRef}
        className={cn(
          'transition-all duration-200',
          isOver && 'bg-primary/10 ring-2 ring-primary rounded-md'
        )}
      >
        <div className="group relative flex items-center -ml-4">
          {/* Chevron for expand/collapse - outside button */}
          {hasSubfolders && onToggleExpand ? (
            <span
              onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
              className="cursor-pointer p-0.5 hover:bg-muted rounded flex-shrink-0"
            >
              {expanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </span>
          ) : (
            <span className="w-4 flex-shrink-0" />
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'w-full justify-start h-8 text-xs pr-8 min-w-0',
                  isOver && 'bg-primary/5'
                )}
                onClick={onClick}
              >
                {isOver ? (
                  <FolderOpen className="h-3 w-3 mr-2 text-primary flex-shrink-0" />
                ) : (
                  <Users className="h-3 w-3 mr-2 flex-shrink-0" />
                )}
                <span className="flex-1 text-left truncate min-w-0 max-w-[100px]">{teamName}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{teamName}</p>
            </TooltipContent>
          </Tooltip>
          {/* Count and menu icon in same position */}
          {workflowCount !== undefined && workflowCount > 0 && (
            <span className="absolute right-3 text-xs text-muted-foreground group-hover:hidden w-5 text-center">
              {workflowCount}
            </span>
          )}
          {onAddFolder && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-3 h-5 w-5 bg-background opacity-0 group-hover:opacity-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onAddFolder}>
                  <FolderPlus className="mr-2 h-4 w-4" />
                  Add Folder
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      {isOver && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 border-2 border-primary border-dashed rounded-md animate-pulse" />
        </div>
      )}
    </div>
  );
}
